import React, { useEffect, useState } from 'react';
import { Dialog } from '@headlessui/react';
import ReactMarkdown from 'react-markdown';
import { lessonPlansApi } from '../../../services/apiV2';
import { StepRenderer } from '../StepRenderer';

export function LessonSequencePreview({ title, planIds, onClose }: { title: string; planIds: number[]; onClose: () => void }) {
  const [plans, setPlans] = useState<any[]>([]);
  const [error, setError] = useState('');
  const [view, setView] = useState('prepare');
  const key = planIds.join(',');
  useEffect(() => {
    let active = true;
    setPlans([]); setError('');
    Promise.all(key.split(',').filter(Boolean).map(id => lessonPlansApi.get(Number(id))))
      .then(results => { if (active) setPlans(results.map(r => r.data)); })
      .catch(() => { if (active) setError('Could not load the full lesson. Close and retry; no activities were changed.'); });
    return () => { active = false; };
  }, [key]);
  return <Dialog open onClose={onClose} className="relative z-50">
    <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
    <div className="fixed inset-0 overflow-y-auto p-4"><Dialog.Panel className="mx-auto max-w-4xl rounded-2xl bg-white p-6 text-slate-900 shadow-xl">
      <div className="flex items-start justify-between gap-4"><Dialog.Title className="text-2xl font-semibold">{title}</Dialog.Title><button onClick={onClose} className="rounded border px-3 py-2">Close preview</button></div>
      <p className="my-3">Read-only lesson preview. Nothing is assigned or started. Student view hides preparation notes; it is not a live student session.</p>
      <div className="mb-4 flex gap-3" role="group" aria-label="Preview audience">
        <button aria-pressed={view === 'prepare'} onClick={() => setView('prepare')} className="rounded border px-3 py-2">Prepare to teach</button>
        <button aria-pressed={view === 'student'} onClick={() => setView('student')} className="rounded border px-3 py-2">Student content</button>
      </div>
      {error && <p role="alert">{error}</p>}
      {!plans.length && !error && <p role="status">Loading lesson…</p>}
      {plans.map((plan, index) => <section key={`${plan.id}-${index}`} className="my-5 rounded-xl border border-purple-200 p-4">
        <h2 className="text-xl font-semibold">{index + 1}. {plan.title}</h2>
        {view === 'prepare' && <>
          <p className="my-2">{plan.description}</p>
          {!!plan.resources?.length && <details><summary>Attached materials</summary>{plan.resources.map((r: any, i: number) => <p key={i}>{r.title || r.name || 'Resource'} — open the Library activity for access.</p>)}</details>}
        </>}
        {(plan.steps || []).map((step: any) => <div key={step.id || step.client_id} className="my-4 border-t pt-4">
          <StepRenderer title={step.title} content={step.content} />
          {step.signal_config?.prompt && <div className="my-3 rounded-lg border p-3"><p className="font-medium">{step.signal_config.prompt}</p>
            <ul className="mt-2 space-y-2">{(step.signal_config.options || []).map((option: any, i: number) => <li key={i}>{String.fromCharCode(65 + i)}. {typeof option === 'string' ? option : option.text || option.label}</li>)}</ul>
            <p className="mt-2 text-sm text-slate-500">Preview only — answers are not recorded.</p>
          </div>}
          {view === 'prepare' && step.content?.teacher_notes && <details className="mt-3 rounded-lg bg-purple-50 p-3"><summary>Teacher notes, preparation &amp; feedback</summary><ReactMarkdown>{step.content.teacher_notes}</ReactMarkdown></details>}
        </div>)}
      </section>)}
    </Dialog.Panel></div>
  </Dialog>;
}
